import React, { useContext, useEffect, useState, useCallback, ChangeEvent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setEditingTheme, upsertTheme } from '../../../../stores/actions/themes';
import Heading from '../../../general-components/v2/heading';
import SlidingMenu, { SlidingMenuOption } from '../../../general-components/sliding-menu';
import EditThemeColorTab from './edit-theme-color-tab';
import FontsTab from './fonts-tab';
import { GreenroomState } from '../../../../stores/types/types';
import classNames from 'classnames';
import { FormContext } from '../../../../contexts/generated-content/content-form-context';
import Portal from '../../../general-components/portal';
import TextInput from '../../../general-components/text-input';
import { Theme } from '../../../../stores/types/themes';
import GenerateContentTitle from '../../generate-content-title';
import { RegenerateStaleContentForMeeting } from '../../../../connection/generate-content';
import { FontAsset, KeyedFontAsset } from '../../../../stores/types/font-assets';
import { GetThemeFonts, SetThemeFont } from '../../../../connection/themes';
import { Field } from '../../../types/stylepacks';
import { isEqual } from 'underscore';
import { canonicalThemeFontKey, resolveThemeFont } from '../../../../utils/theme-fonts';
import logger from '../../../../utils/logger-rum';

export enum EDIT_THEME_SUBTAB {
	COLORS = 'Colors',
	FONTS = 'Fonts',
}

const slidingMenuOptions: SlidingMenuOption[] = [
	{label: EDIT_THEME_SUBTAB.COLORS},
	{label: EDIT_THEME_SUBTAB.FONTS}
]

interface EditsState {
	anyEdits: boolean;
	name?: boolean;
	fonts?: boolean;
	colors?: boolean;
}

export default function EditThemeTab() {
	const dispatch = useDispatch();
	const isEditing = useSelector((s: GreenroomState) => s.ThemesReducer.themeEditing);
	const theme = useSelector((s: GreenroomState) => s.ThemesReducer.viewedTheme);
	const channel = useSelector((s: GreenroomState) => s.SessionReducer.meeting?.channel);
	const meeting = useSelector((s: GreenroomState) => s.SessionReducer.meeting?.meeting);
	const defaultThemes = useSelector((s: GreenroomState) => s.ThemesReducer.defaultThemes);

	const [tab, setTab] = useState(slidingMenuOptions[0]);

	const [unEditedTheme, setUnEditedTheme] = useState<Partial<Theme>>({colors:[],name:''});
	const [unEditedFonts, setUnEditedFonts] = useState<KeyedFontAsset>({});
	
	const [loadingFonts, setLoadingFonts] = useState(true);
	const [editedColors, setEditedColors] = useState<string[]>(theme?.colors ?? []);
	const [editedFonts, setEditedFonts] = useState<KeyedFontAsset>({});
	
	const [name, setName] = useState('');
	const [showNameModal, setShowNameModal] = useState(false);
	const [hasEdits, setHasEdits] = useState<EditsState>({anyEdits: false});

	const {
		currTemplate,
		applyThemeToItem,
		onChange,
		currrentThemeId,
	} = useContext(FormContext);

	const fontFields = useMemo((): Field[] =>
		(currTemplate.data.variables ?? []).reduce((curr: Field[], acc: Field) => {
			if (acc.type === 'font') {
				curr.push(acc);
			}
			return curr;
		}, []), 
		[currTemplate?.data?.variables]
	);

	const loadThemeFonts = async (themeId: number, meetingId: number) => {
		setUnEditedFonts({})
		setEditedFonts({})
		const fonts = await GetThemeFonts(meetingId, themeId);
		if (fonts) {
			const fontsInUse: KeyedFontAsset = {};
			// filter out fonts on theme that dont have fields on this item
			fontFields.forEach((acc: Field, index: number) => {
				const fieldName = acc.userData as string;
				const themeFont = resolveThemeFont(fonts, fieldName, index);
				if (themeFont) {
					fontsInUse[fieldName] = themeFont;
				}
			})
			setEditedFonts(fontsInUse)
			setUnEditedFonts(fontsInUse);
		}
		setLoadingFonts(false);
	};

	useEffect(() => {
		if (theme) {
			setUnEditedTheme({colors: [...theme.colors], name: theme.name});
			setEditedColors([...theme.colors]);
			setName(`${theme.name}`);
			setHasEdits({anyEdits: false});
			// a draft theme has id 0, which the fonts endpoint rejects — inherit
			// from the theme the draft was cloned off of instead
			const fontSourceThemeId = theme.id || currrentThemeId;
			if (meeting && fontSourceThemeId) {
				setLoadingFonts(true);
				loadThemeFonts(fontSourceThemeId, meeting);
			} else {
				setUnEditedFonts({});
				setEditedFonts({});
				setLoadingFonts(false);
			}
		}
	}, [theme, meeting]);

	const applyFontsToItem = (meetingId: number, themeId: number, fonts: KeyedFontAsset) => {
		fontFields.forEach((field: Field, index: number) => {
			const font = fonts[field.userData];
			if (!font?.url) return;
			const event = {target: {name: field.userData, value: font.url}};
			onChange(event as ChangeEvent<HTMLInputElement>)
			SetThemeFont(meetingId, themeId, font.id, canonicalThemeFontKey(field.userData, index))
		})
		setEditedFonts((old) => ({...old, ...fonts}));
		setUnEditedFonts((old) => ({...old, ...fonts}));
	}

	const editSelectedFont = useCallback((font: FontAsset, fieldKey: string) => {
		onChange({target: {name:fieldKey, value: font.url}} as ChangeEvent<HTMLInputElement>)
		setEditedFonts((old) => ({...old, [fieldKey]: font}))
		setHasEdits((old) => ({...old, anyEdits: true, fonts: true}));
	}, []);

	const saveForReal = useCallback(async () => {
		if (!channel) {
			logger.error('Could not save theme, no channel');
			return;
		}
		setShowNameModal(false);
		const nTheme: Partial<Theme> = {
			...(theme ?? {}),
			name: name,
			colors: editedColors,
			stylepack: currTemplate.name
		};
		
		// for now, if the theme is a default theme, remove the id to force a new
		if (defaultThemes.some(x => x.id === nTheme.id)) {
			nTheme.id = 0;
		}

		// Create the action and await its promise
		const action = upsertTheme(channel, nTheme);
		dispatch(action);

		try {
			const savedTheme = await action.promise;
			if (savedTheme && meeting) {
				applyFontsToItem(meeting, savedTheme.id, editedFonts);
				RegenerateStaleContentForMeeting(meeting, { themeIds: [savedTheme.id] });
			}
		} catch (error) {
			logger.error('Failed to save theme:', error);
		}
	}, [channel, name, editedColors, currTemplate.name, theme, editedFonts]);

	const onSave = useCallback(() => {
		if (isEditing) {
			saveForReal();
		} else {
			if (theme?.name === name) {
				// if creating a theme and they have not changed the name,
				// set to blank so modal will prompt for name
				setName('');
			}
			setShowNameModal(true);
		}
	}, [saveForReal, isEditing, name, theme?.name]);

	const editColorSwatch = useCallback((color: string, index: number) => {
		if (!editedColors?.length) return;
		editedColors[index] = color.toUpperCase();
		setEditedColors(editedColors);
		setHasEdits((old) => ({...old, anyEdits: true, colors: true}));
		applyThemeToItem({...theme, colors: editedColors});
	}, [editedColors, theme?.colors, unEditedTheme?.colors]);

	const backDropClicked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		setShowNameModal(false);
		e.stopPropagation();
	}

	const onResetFonts = useCallback(() => {
		for (const [fieldKey, font] of Object.entries(unEditedFonts)) {
			editSelectedFont(font, fieldKey)
		}	
		setHasEdits((old) => ({
			...old,
			anyEdits: !!(old.colors || old.name),
			fonts: false
		}));
	}, [unEditedFonts, editedFonts])
	
	const onCancel = useCallback(() => {
		// check if fonts and colors have been edited before regenerating content
		if (!isEqual(editedFonts, unEditedFonts)) onResetFonts();
		if (editedColors.some((c, i) => c !== theme?.colors?.[i])) applyThemeToItem(unEditedTheme);
		setHasEdits({anyEdits: false});
		dispatch(setEditingTheme(null, false));
	}, [
		unEditedTheme, 
		applyThemeToItem, 
		onResetFonts, 
		editedColors, 
		theme?.colors, 
		editedFonts, 
		unEditedFonts
	]);

	return (<div className={classNames('editing-theme-tab-container', {open: !!theme})}>
		<div className='edit-theme-tab'>
			<Heading
				backButtonFunction={onCancel}
				showXButton={true}
				title={<GenerateContentTitle
						key={theme?.id}
						title={name ?? theme?.name}
						placeholder='My new style'
						allowBlank
						saveCallback={(newName: string) => {
							if (name !== newName) {
								setHasEdits((old) => ({...old, anyEdits: true, name: true}));
								setName(newName);
							}
						}}
					/>}
			/>
			<SlidingMenu 
				options={slidingMenuOptions}
				selectedOption={tab}
				callback={setTab}
				className='position-relative width-calc-full'
				fullWidthDivider={true}
				dividerClassname={'lemonade'}
			/>
			<div>
				<h6 className='bl-font-header edit-theme-tab-header'>In this Stylepack</h6>
			</div>
			<div className='edit-theme-swatches'>
			{tab.label === EDIT_THEME_SUBTAB.COLORS 
				? <EditThemeColorTab 
					editColor={editColorSwatch}
					editedColors={editedColors}
					hasEdits={!!hasEdits.colors}
				/>
				: <FontsTab 
					onSelect={editSelectedFont}
					selectedFonts={editedFonts}
					theme={theme}
					loadingThemeFonts={loadingFonts}
					fontFields={fontFields}
					resetFonts={onResetFonts}
					hasEdits={!!hasEdits.fonts}
				/>}
				<div className='save-buttons flex-center'>
					<button onClick={onCancel} className='button-pillbox dullgray bl-font-l1'>Cancel</button>
					<button onClick={onSave} disabled={!hasEdits.anyEdits} className='button-pillbox sherbert bl-font-l1'>Save</button>
				</div>
			</div>
			{showNameModal ? <Portal targetId='right-click-portal'>
				<div className='name-theme-modal-backdrop fade-in-fast' onClick={backDropClicked}/>
				<div className='name-theme-modal fade-in-fast' onClick={(e) => e.stopPropagation()}>
					<h4 className='bl-font-header'>Give your style a name</h4>
					<TextInput placeholder={'My new style'} value={name ?? ''} onChange={(e) => setName(e.target.value)} required={true}/>
					<div className='flex-center'>
						<button className='button-pillbox dullgray cancel-theme-button' onClick={() => setShowNameModal(false)}>Cancel</button>
						<button className='button-pillbox sherbert' onClick={saveForReal} disabled={name === ''}>Save</button>
					</div>
				</div>
			</Portal> : null}
		</div>
	</div>);
}
